import { useEffect, useRef } from "react";
import { useAuth } from "@workos-inc/authkit-react";
import { toast } from "sonner";
import { isElectronRuntime } from "@/lib/logout";
import { getAccessTokenWithRetry } from "@/lib/auth-token";
import { regionalApiUrl } from "@/lib/regional-api-url";
import { IS_LOCAL_DEV } from "@/lib/config";
import { ANO_CLI_PENDING_FLAG } from "@/lib/cli-creds-flag";
import type { ElectronPtyApi } from "@/components/terminal/shell-terminal/electron-pty";

interface WorkspaceLike {
  id: string;
  name?: string | null;
  slug?: string | null;
  region?: string | null;
}

interface UserLike {
  id: string;
}

/**
 * Auto-install ~/.config/ano/credentials.json so external terminals
 * (Terminal.app, iTerm, Cursor, Claude Code) can run `ano` commands.
 * Two flows share this effect:
 *   1. First install: onboarding's "Install Ano CLI" step set the pending
 *      flag. On the user's first non-null selectedWorkspace, install +
 *      toast. Pending flag is cleared on success.
 *   2. Workspace switch (Option A — "follow the app"): when the managed
 *      workspace in credentials.json differs from selectedWorkspace,
 *      silently rewrite. No toast. Cache layer in main process keeps
 *      most rewrites round-trip-free.
 * Web build / non-Electron renderer: effect is a no-op.
 */
export function useCliCredentialsInstall({
  selectedWorkspace,
  workspaceList,
  user,
}: {
  selectedWorkspace: string | null;
  workspaceList: ReadonlyArray<WorkspaceLike>;
  user: UserLike | null | undefined;
}) {
  const { getAccessToken: getCliAccessToken } = useAuth();
  const cliInstallInFlightRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isElectronRuntime()) return;
    // dev:local mints a stub `local-dev-token` (not a real JWT) so the JWT
    // sub-extraction in the main process fails with `invalid-token` and
    // toasts on every workspace selection. There's no real workspace API
    // key to mint here anyway.
    if (IS_LOCAL_DEV) return;
    if (!selectedWorkspace || !user) return;
    if (cliInstallInFlightRef.current === selectedWorkspace) return;

    const ws = workspaceList.find((w) => w.id === selectedWorkspace);
    if (!ws) return;
    const wsApiUrl = regionalApiUrl(ws.region);

    // Set the in-flight guard BEFORE the awaits so re-entry on rapid
    // selectedWorkspace transitions doesn't double-fire. The cleanup
    // path (finally) clears it back to null.
    cliInstallInFlightRef.current = selectedWorkspace;

    void (async () => {
      try {
        const api = (window as { __electronApi?: ElectronPtyApi })
          .__electronApi;
        if (!api?.cliCredentialsInstall || !api?.cliCredentialsStatus) return;

        let status: Awaited<
          ReturnType<NonNullable<ElectronPtyApi["cliCredentialsStatus"]>>
        >;
        try {
          status = await api.cliCredentialsStatus();
        } catch (err) {
          // Don't swallow silently — surface for debugging. Still bail
          // on this transition; we'll retry on the next workspace entry.
          console.error("[cli-credentials] status check failed", err);
          return;
        }
        // localStorage can throw in private-browsing modes, on quota
        // overflow, or when the storage backend is corrupted. Treat any
        // failure as "not pending" so the install still happens — we
        // just won't toast for first-time setup. Better than retrying
        // forever in silence.
        let pending = false;
        try {
          pending = localStorage.getItem(ANO_CLI_PENDING_FLAG) === "true";
        } catch (err) {
          console.warn(
            "[cli-credentials] localStorage unavailable, treating as not pending",
            err,
          );
        }

        // Skip when EVERYTHING already matches: workspace, user, AND
        // endpoint. Previous condition checked only workspaceId — that
        // missed:
        //   - endpoint mismatch: DMG was rebuilt against a different
        //     API URL (staging → prod) but managed manifest still
        //     points at the old endpoint. Workspace IDs happen to
        //     match → skip → CLI stays on old endpoint forever.
        //   - userId mismatch: same human re-signed-up after account
        //     delete (new WorkOS user_id) but selected the same
        //     workspace by coincidence. Skip → CLI stays minted for
        //     the old identity (now-deleted API key).
        // Either of these chains leaves the CLI silently pointing at
        // the wrong place with no toast and no recovery short of
        // manual `rm ~/.config/ano/credentials.json`.
        const managedMatchesFully =
          status.managed?.workspaceId === selectedWorkspace &&
          status.managed?.userId === user.id &&
          status.managed?.endpoint === wsApiUrl;
        if (!pending && managedMatchesFully) {
          return;
        }

        const accessToken = await getAccessTokenWithRetry(getCliAccessToken);
        if (!accessToken) return;

        const result = await api.cliCredentialsInstall({
          accessToken,
          workspaceId: selectedWorkspace,
          workspaceName: ws.name ?? "",
          apiUrl: wsApiUrl,
        });

        // Best-effort localStorage clear — same fault-tolerance reasoning
        // as the read above. If this throws we just leave the flag set;
        // next workspace switch sees a successful manifest match and
        // skips the install.
        const clearPendingFlag = () => {
          try {
            localStorage.removeItem(ANO_CLI_PENDING_FLAG);
          } catch {
            // localStorage unavailable — flag stays, but the manifest
            // now matches selectedWorkspace so the next switch's
            // `status.managed?.workspaceId === selectedWorkspace`
            // check returns early.
          }
        };

        if (result.ok) {
          if (pending) {
            clearPendingFlag();
            // Include workspace name so multi-workspace users know which
            // workspace the CLI was set up against.
            toast.success(
              ws.name
                ? `Ano CLI is ready for ${ws.name} in your terminal`
                : "Ano CLI is ready in your terminal",
            );
          }
          // Else: silent workspace-switch rewrite — no toast.
        } else {
          // ALL failure modes — provision-failed, write-failed,
          // invalid-token, symlink, mode-too-wide, owner-mismatch,
          // invalid-api-url — toast loudly. Previous behavior was
          // console.error-only for everything except
          // existing-default-not-ours (now an unreachable code path
          // since inspectBeforeWrite always allows). Silent failures
          // were the root cause of the "CLI stuck on staging even
          // after I entered a workspace" bug — the user had no signal
          // that anything went wrong.
          console.error("[cli-credentials] install failed:", result.reason);
          toast.error(
            `Ano CLI setup failed (${result.reason}). Run \`ano auth login --workspace-id ${selectedWorkspace}\` manually, or re-launch Ano.`,
          );
        }
      } catch (err) {
        console.error("[cli-credentials] install failed", err);
      } finally {
        cliInstallInFlightRef.current = null;
      }
    })();
  }, [selectedWorkspace, workspaceList, user, getCliAccessToken]);
}
